import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ExternalLink, Sparkles, Activity, Loader2, ArrowLeft } from 'lucide-react'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import ScoreRing from '@/components/ScoreRing'
import CategoryAccordion from '@/components/CategoryAccordion'
import SuggestionCard from '@/components/SuggestionCard'
import StreamingSuggestionCard from '@/components/StreamingSuggestionCard'
import ReRunAuditButton from '@/components/ReRunAuditButton'
import PrivacyBanner from '@/components/PrivacyBanner'
import { AuditRunningCard, AuditErrorCard } from '@/components/AuditStatusCards'
import { useAuditProgress } from '@/hooks/useAuditProgress'
import { useAiSuggestions } from '@/hooks/useAiSuggestions'

type ResultsTab = 'scores' | 'ai'

interface AuditPageState {
  /** When set, AuditPage redirects to /compare/{compareWithId}/{id} on completion. */
  compareWithId?: string
}

export default function AuditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const compareWithId = (location.state as AuditPageState | null)?.compareWithId
  const { phase, progress, message, result } = useAuditProgress(id)
  const ai = useAiSuggestions(id)

  // Diff-aware re-run: if we landed here from a Re-run button, redirect to the
  // comparison view as soon as this audit completes. Only fires once per mount.
  useEffect(() => {
    if (phase === 'complete' && result && compareWithId && id && compareWithId !== id) {
      navigate(`/compare/${compareWithId}/${id}`, { replace: true })
    }
  }, [phase, result, compareWithId, id, navigate])

  const [tab, setTab] = useState<ResultsTab>('scores')
  const showAi = ai.suggestions.length > 0 || ai.drafts.length > 0 || ai.errors.length > 0 || ai.streaming === false

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto px-4 py-12">

        {/* ── Progress state ─────────────────────────────────────────────── */}
        {phase !== 'complete' && phase !== 'error' && (
          <AuditRunningCard message={message} progress={progress} />
        )}

        {/* ── Error state ────────────────────────────────────────────────── */}
        {phase === 'error' && (
          <AuditErrorCard message={message || 'Audit failed.'} actionLabel="Try again" onAction={() => navigate('/')} />
        )}

        {/* ── Results state ──────────────────────────────────────────────── */}
        {phase === 'complete' && result && (
          <div className="space-y-4">

            {/* Back + title */}
            <div className="flex items-center justify-between mb-2 gap-2">
              <button
                onClick={() => navigate('/')}
                className="text-sm flex items-center gap-1 transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <ArrowLeft size={14} /> New audit
              </button>
              <div className="flex items-center gap-2">
                <ReRunAuditButton report={result} label="Re-run & compare" />
                <button
                  onClick={() => navigate('/reports')}
                  className="text-sm flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  All reports <ExternalLink size={12} />
                </button>
              </div>
            </div>

            {/* Score rings */}
            <GlassCard>
              <p className="text-xs mb-1 truncate" style={{ color: 'var(--text-muted)' }}>
                {result.device} · {new Date().toLocaleString()}
              </p>
              <h2 className="text-sm font-semibold mb-6 break-all" style={{ color: 'var(--text-secondary)' }}>
                {result.url}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 justify-items-center">
                <ScoreRing score={result.performanceScore}    label="Performance" />
                <ScoreRing score={result.accessibilityScore}  label="Accessibility" />
                <ScoreRing score={result.bestPracticesScore}  label="Best Practices" />
                <ScoreRing score={result.seoScore}            label="SEO" />
              </div>
            </GlassCard>

            {/* Tab switcher: only shown when AI ran */}
            {showAi && (
              <div className="flex gap-1.5 px-1">
                <TabButton active={tab === 'scores'} onClick={() => setTab('scores')}
                  icon={<Activity size={12} />} label="Audit details" />
                <TabButton active={tab === 'ai'} onClick={() => setTab('ai')}
                  icon={<Sparkles size={12} />}
                  label={
                    ai.streaming
                      ? `AI suggestions · ${ai.suggestions.length}${ai.drafts.length ? `+${ai.drafts.length}` : ''} so far`
                      : `AI suggestions${ai.suggestions.length ? ` · ${ai.suggestions.length}` : ''}`
                  }
                  streaming={ai.streaming} />
              </div>
            )}

            {/* Scores tab (default) */}
            {(!showAi || tab === 'scores') && result.rawReport && (
              <div className="space-y-2">
                {!showAi && (
                  <h3 className="text-xs font-semibold uppercase tracking-wider px-1 mb-3"
                    style={{ color: 'var(--text-muted)' }}>
                    Audit Details
                  </h3>
                )}
                <CategoryAccordion rawReport={result.rawReport} />
              </div>
            )}

            {/* AI suggestions tab */}
            {showAi && tab === 'ai' && (
              <div className="space-y-3">
                <PrivacyBanner />
                {ai.suggestions.map(s => (
                  <SuggestionCard key={s.id ?? `${s.auditCheckId}-${s.createdAt}`} suggestion={s} />
                ))}
                {ai.drafts.map(d => (
                  <StreamingSuggestionCard key={`draft-${d.auditCheckId}`} draft={d} />
                ))}
                {ai.streaming && ai.drafts.length === 0 && (
                  <GlassCard className="text-center py-6">
                    <Loader2 size={20} className="mx-auto animate-spin text-accent mb-2" />
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Generating suggestions… (depends on model size and hardware)
                    </p>
                  </GlassCard>
                )}
                {ai.errors.length > 0 && (
                  <GlassCard className="py-3">
                    <p className="text-xs font-semibold mb-1 text-red-400">
                      {ai.errors.length} suggestion{ai.errors.length === 1 ? '' : 's'} failed
                    </p>
                    <ul className="text-[11px] list-disc list-inside space-y-0.5"
                      style={{ color: 'var(--text-muted)' }}>
                      {ai.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </GlassCard>
                )}
                {!ai.streaming && ai.suggestions.length === 0
                    && ai.drafts.length === 0 && ai.errors.length === 0 && (
                  <GlassCard className="text-center py-6">
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      No suggestions were generated for this audit.
                    </p>
                  </GlassCard>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PageTransition>
  )
}

function TabButton({
  active, onClick, icon, label, streaming = false,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  streaming?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
        active
          ? 'bg-accent/15 border-accent/40 text-accent'
          : 'bg-white/5 border-white/10 text-[var(--text-secondary)] hover:border-white/20'
      }`}
    >
      {icon}
      {label}
      {streaming && (
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent" />
        </span>
      )}
    </button>
  )
}
